// Reusable form primitives ,  TextField, Checkbox, Footer, AuthDecor.
// Stripped-down port of the equivalents in vivid360-onboard/src/primitives.jsx.

import { useEffect, useRef } from 'react';
import { I, VividMark, VividWordmark } from './icons.jsx';
import { translate } from './i18n.jsx';

// Form-level alert for a failed request (ENG-502, AUTH-502). Request errors
// belong here, above the submit control, not on a field: a rate limit or a
// server fault is not a password problem. It takes focus when a message
// appears so a screen reader announces it and the keyboard lands on it.
export function FormAlert({ message, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (message) ref.current?.focus();
  }, [message]);
  if (!message) return null;
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      className={`banner banner-danger flex items-start gap-2 outline-none ${className}`}
    >
      <span className="shrink-0 pt-0.5"><I.AlertCircle size={14} /></span>
      <span className="flex-1">{message}</span>
    </div>
  );
}

// ``t`` (from ``useLang``) translates the shared chrome on the pages that
// have a language; without it they stay English (the sign-in page).
const english = (key, vars) => translate('en', key, vars);

export function TextField({
  label, type = 'text', value, onChange, onBlur,
  placeholder, error, helper, autoComplete, rightAdornment, name, readOnly,
}) {
  return (
    <label className="block">
      {label && <span className="block mb-1.5 text-[12px] font-medium text-stone-700">{label}</span>}
      <div className="relative">
        <input
          type={type}
          name={name}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete={autoComplete}
          readOnly={readOnly}
          aria-readonly={readOnly || undefined}
          className={`field-input ${error ? 'error' : ''} ${rightAdornment ? 'pe-10' : ''}`}
        />
        {rightAdornment && (
          <div className="absolute inset-y-0 end-2 flex items-center text-stone-500">
            {rightAdornment}
          </div>
        )}
      </div>
      {error && (
        <span className="mt-1.5 flex items-center gap-1 text-[12px] text-red-600">
          <I.AlertCircle size={12}/>{error}
        </span>
      )}
      {!error && helper && (
        <span className="mt-1.5 block text-[12px] text-stone-500">{helper}</span>
      )}
    </label>
  );
}

export function Checkbox({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
        checked ? 'bg-ink border-ink text-white' : 'bg-white border-stone-300 hover:border-stone-400'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {checked && <I.Check size={11} stroke={3}/>}
    </button>
  );
}

export function Footer({ t = english }) {
  return (
    <div className="h-16 flex items-center justify-between text-[12px] text-stone-500">
      <span>© Vivid360</span>
      <div className="flex gap-4">
        <a href="https://www.vivid360.io/privacy" className="hover:text-ink">{t('footer.privacy')}</a>
        <a href="https://www.vivid360.io" className="hover:text-ink">vivid360.io</a>
      </div>
    </div>
  );
}

// Decorative right-side panel for the split-screen auth pages.
// Stays visually quiet so it does not compete with the form.
export function AuthDecor({ t = english }) {
  return (
    <div className="h-full w-full  from-stone-50 to-white border-s border-stone-200 relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center max-w-sm px-8">
          <VividMark size={64} />
          <h2 className="mt-6 text-[24px] font-bold tracking-tight text-ink">
            {t('decor.title')}
          </h2>
          <p className="mt-3 text-[14px] text-stone-500 leading-relaxed">
            {t('decor.body')}
          </p>
        </div>
      </div>
    </div>
  );
}

export { VividWordmark };
